'use client';

import React, { useState } from 'react';

import Image from 'next/image';

import ReportIcon from '@/assets/images/report-icon.png';
import { useAuthContext } from '@/components/Providers/AuthProvider';
import Button from '@/components/V2/Button';
import { useGetEzShipProjectionDetailsQuery } from '@/store/api/dashboard';

import ButtonLink from '../ButtonLink';
import ReportsLoader from '../Reports/CustomerDetailModal/ReportsLoader';
import VolumeBucketForEZPVModels from '../Reports/VolumeBucketModals/VolumeBucketForEZPVModels';
import VolumeBucketModels from '../Reports/VolumeBucketModals/VolumeBucketModels';
import BarChartWithTopFixedTooltip from './Components/BarChartWithTopFixedTooltip';

const EzshipPVProjection = () => {
  const { customer } = useAuthContext();
  const customerId = customer?.customerID ?? 0;

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEZModalOpen, setIsEZModalOpen] = useState(false);
  const [volumeBucketLabel, setVolumeBucketLabel] = useState('');
  const [isTableView, setIsTableView] = useState(false);
  const [volumeBucket, setVolumeBucket] = useState<number>();
  const { data, isLoading, isFetching } = useGetEzShipProjectionDetailsQuery();

  const PROCESSED_MTD = 'PROCESSED MTD';

  return (
    <div className="v2-shadow-md v2-p-[24px] md:v2-p-[24px] sm:v2-p-4 xs:v2-p-4 v2-bg-white v2-rounded-xs">
      <div className="v2-border-b v2-border-[#E5E5E5] v2-flex v2-items-center v2-justify-between iphone12-fix  v2-w-full v2-pt-[30px] v2-pb-5">
        <h2 className="v2-text-[#333333] v2-font-sweet-sans-pro-bold v2-font-bold v2-text-xl v2-text-center">
          EZ Ship PV Projection
        </h2>
        <ButtonLink
          scheme="primary"
          variant="outlined"
          className="!v2-p-0 !v2-border-0 !v2-bg-transparent v2-flex v2-items-center v2-gap-[6px] !v2-w-fit"
          href="/pending-ezship-orders"
        >
          <Image src={ReportIcon} alt="" />
        </ButtonLink>
      </div>

      {isLoading ? (
        <div className="v2-h-[296px] v2-flex v2-justify-center v2-items-center">
          <ReportsLoader />
        </div>
      ) : (
        <div className="v2-mb-[20px]">
          <BarChartWithTopFixedTooltip
            barHeight={data?.data?.barHeight ?? []}
            months={data?.data?.months ?? []}
            ezshipprocessed={data?.data?.ezShipProcessed ?? 0}
            ezshipunprocessed={data?.data?.ezShipUnprocessed ?? 0}
            onBarItemClick={d => {
              const str = d.dataIndex ? `(MONTH ${d.dataIndex})` : 'Month';
              if (d.seriesId === 'uvId') {
                setIsEZModalOpen(true);
                setVolumeBucketLabel(d.dataIndex === 0 ? 'REMAINDER OF THE MONTH' : `PV NEXT THREE ${str}`);
                setIsTableView(true);
                setVolumeBucket(d.dataIndex ? d.dataIndex + 1 : d.dataIndex);
              } else {
                setIsModalOpen(true);
                setIsTableView(false);
                setVolumeBucketLabel(PROCESSED_MTD);
                setVolumeBucket(25);
              }
            }}
          />
          <div className="v2-flex v2-items-center v2-gap-[20px] v2-mt-[20px]">
            <div className="v2-flex v2-items-center v2-gap-2">
              <div className="v2-h-[20px] v2-w-[20px] v2-bg-primary"></div>
              <p className="v2-font-sweet-sans-pro v2-font-normal v2-text-base v2-text-[#101828]">Processed</p>
            </div>
            <div className="v2-flex v2-items-center v2-gap-2">
              <div className="v2-h-[20px] v2-w-[20px] v2-bg-[#E08D6E]"></div>
              <p className="v2-font-sweet-sans-pro v2-font-normal v2-text-base v2-text-[#101828]">Not Processed</p>
            </div>
          </div>
        </div>
      )}
      <div className="v2-flex v2-flex-col v2-gap-2 v2-font-sweet-sans-pro v2-font-normal v2-text-base v2-text-[#333333]">
        <div className="v2-flex v2-items-center v2-justify-between">
          <p className="">Processed month to date:</p>
          <Button
            btnSize="sm"
            scheme="text"
            type="button"
            variant="outlined"
            className="!v2-p-0 !v2-bg-transparent v2-text-primary v2-underline v2-flex v2-items-center"
            onClick={() => {
              setIsModalOpen(true);
              setVolumeBucketLabel(PROCESSED_MTD);
              setIsTableView(false);
              setVolumeBucket(25);
            }}
          >
            ${data?.data.displayProcessedValue ?? 0.0}
          </Button>
        </div>
        <div className="v2-flex v2-items-center v2-justify-between">
          <p className="">Scheduled but not yet processed:</p>
          <Button
            btnSize="sm"
            scheme="text"
            type="button"
            variant="outlined"
            className="!v2-p-0 !v2-bg-transparent !v2-text-[#E08D6E] v2-underline v2-flex v2-items-center"
            onClick={() => {
              setIsEZModalOpen(true);
              setVolumeBucketLabel('REMAINDER OF THE MONTH');
              setIsTableView(true);
              setVolumeBucket(0);
            }}
          >
            ${data?.data.displayUnprocessedValue ?? 0.0}
          </Button>
        </div>
        <div className="v2-flex v2-items-center v2-justify-between">
          <p className="">Projected Total for this month:</p>
          <p className="">${data?.data.totalEzshipValue ?? 0.0}</p>
        </div>
      </div>

      <VolumeBucketModels
        modalOpen={isModalOpen}
        setModalOpen={() => setIsModalOpen(false)}
        customerId={customerId}
        volumeBucketLabel={volumeBucketLabel}
        volumeBucket={volumeBucket}
        isTableView={isTableView}
        selectedPeriodid={undefined}
      />

      <VolumeBucketForEZPVModels
        modalOpen={isEZModalOpen}
        setModalOpen={() => setIsEZModalOpen(false)}
        volumeBucketLabel={volumeBucketLabel}
        volumeBucket={1}
        isTableView={isTableView}
        selectedMonth={volumeBucket}
        isMonthBar={true}
      />
    </div>
  );
};

export default EzshipPVProjection;
